import React, { useState, useRef } from 'react';
import { FileText, Download, Mail, Eye, Trash2, XCircle, RefreshCw } from 'lucide-react';
import { usePayroll } from '../hooks/usePayroll';
import { useEmployees } from '../hooks/useEmployees';
import { usePayPeriodSettings } from '../hooks/usePayPeriodSettings';
import { useCompanySettings } from '../hooks/useCompanySettings';
import { useACHPolling } from '../hooks/useACHPolling';
import PayStubModal from './PayStubModal';
import { generatePayStubPDF } from '../utils/pdfGenerator';
import { sendPayStubEmail } from '../utils/emailService';
import { parseLocalDateForDisplay, calculatePeriodNumber } from '../utils/payrollPeriodCalculator';
import { supabase } from '../lib/supabase';
import type { PayrollEntry } from '../types';

const PayrollHistory: React.FC = () => {
  const { payrollEntries, loading, refetch } = usePayroll();
  const { employees } = useEmployees();
  const { settings: payPeriodSettings } = usePayPeriodSettings();
  const { settings: companySettings } = useCompanySettings();
  const { isPolling, isCheckingNow, lastUpdate, activeTransactionCount, pollNow } = useACHPolling();
  const [selectedEntry, setSelectedEntry] = useState<PayrollEntry | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [voidError, setVoidError] = useState<string | null>(null);
  const [voidingIds, setVoidingIds] = useState<Set<string>>(new Set());
  const [achTransactions, setAchTransactions] = useState<Record<string, any>>({});

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const filteredPayrollEntries = payrollEntries
    .filter(entry => {
      const periodStart = parseLocalDateForDisplay(entry.payPeriod.start);
      return periodStart >= new Date('2024-12-29');
    })
    .sort((a, b) => {
      const dateA = parseLocalDateForDisplay(a.payPeriod.start);
      const dateB = parseLocalDateForDisplay(b.payPeriod.start);
      return dateB.getTime() - dateA.getTime();
    });

  const fetchAchTransactions = React.useCallback(async () => {
    if (filteredPayrollEntries.length === 0) return;

    console.log('Fetching ACH transactions for', filteredPayrollEntries.length, 'entries');
    const entryIds = filteredPayrollEntries.map(e => e.id);
    const { data, error } = await supabase
      .from('ach_transactions')
      .select('*')
      .in('payroll_entry_id', entryIds);

    if (error) {
      console.error('Error fetching ACH transactions:', error);
    } else if (data) {
      console.log('Fetched', data.length, 'ACH transactions:', data);
      const transactionMap: Record<string, any> = {};
      data.forEach(tx => {
        console.log(`Transaction for entry ${tx.payroll_entry_id}:`, {
          id: tx.id,
          transaction_id: tx.transaction_id,
          status: tx.status,
          amount: tx.amount
        });
        transactionMap[tx.payroll_entry_id] = tx;
      });
      setAchTransactions(transactionMap);
    }
  }, [filteredPayrollEntries]);

  React.useEffect(() => {
    fetchAchTransactions();
  }, [fetchAchTransactions]);

  React.useEffect(() => {
    const channel = supabase
      .channel('ach_transactions_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'ach_transactions',
        },
        (payload) => {
          console.log('ACH transaction changed:', payload);

          if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
            setAchTransactions(prev => ({
              ...prev,
              [payload.new.payroll_entry_id]: payload.new,
            }));
          } else if (payload.eventType === 'DELETE') {
            setAchTransactions(prev => {
              const newMap = { ...prev };
              delete newMap[payload.old.payroll_entry_id];
              return newMap;
            });
          }

          refetch();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [refetch]);

  const formatCurrency = (amount: number) => {
    return amount.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const getPeriodNumber = (entry: PayrollEntry): number => {
    if (!entry.payDate) return 0;
    const payDate = parseLocalDateForDisplay(entry.payDate);
    return calculatePeriodNumber(payDate);
  };

  const getPayYear = (entry: PayrollEntry): number => {
    if (!entry.payDate) return 0;
    const payDate = parseLocalDateForDisplay(entry.payDate);
    return payDate.getFullYear();
  };

  const formatPeriodNumber = (entry: PayrollEntry) => {
    const periodNum = getPeriodNumber(entry);
    const year = getPayYear(entry);
    return `Period ${periodNum} (${year})`;
  };

  const handleEmailPaystub = async (entry: PayrollEntry) => {
    const employee = employees.find(e => e.id === entry.employeeId);
    if (!employee) return;

    try {
      await sendPayStubEmail(entry, employee);
      alert('Paystub sent successfully!');
    } catch (error) {
      console.error('Error sending paystub:', error);
      alert('Failed to send paystub. Please try again.');
    }
  };

  const handleDownloadPDF = async (entry: PayrollEntry) => {
    const employee = employees.find(e => e.id === entry.employeeId);
    if (!employee) return;

    try {
      await generatePayStubPDF(entry, employee, false, companySettings);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  const handleDelete = async (entry: PayrollEntry) => {
    if (!window.confirm('Are you sure you want to delete this payroll entry?')) {
      return;
    }

    try {
      setDeleteError(null);
      const { error } = await supabase
        .from('payroll_entries')
        .delete()
        .eq('id', entry.id);

      if (error) {
        throw new Error(error.message);
      }
      
      await refetch();
    } catch (error) {
      console.error('Error deleting entry:', error);
      setDeleteError(error instanceof Error ? error.message : 'Failed to delete entry');
      alert('Failed to delete entry. Please try again.');
    }
  };

  const handleUpdateStatus = async (entry: PayrollEntry) => {
    try {
      setUpdateError(null);
      const newStatus = entry.status === 'pending' ? 'paid' : entry.status;

      const { error } = await supabase
        .from('payroll_entries')
        .update({ status: newStatus })
        .eq('id', entry.id);

      if (error) {
        throw new Error(error.message);
      }

      await refetch();
    } catch (error) {
      console.error('Error updating status:', error);
      setUpdateError(error instanceof Error ? error.message : 'Failed to update status');
    }
  };

  const handleVoidTransaction = async (entry: PayrollEntry) => {
    const achTx = achTransactions[entry.id];
    if (!achTx || !achTx.transaction_id) {
      alert('No ACH transaction found for this entry.');
      return;
    }

    if (!window.confirm('Are you sure you want to void this ACH transaction? This action cannot be undone.')) {
      return;
    }

    try {
      setVoidError(null);
      setVoidingIds(prev => new Set(prev).add(entry.id));

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/void-ach-transaction`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            transactionId: achTx.transaction_id,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to void transaction');
      }

      alert('Transaction voided successfully!');
      await refetch();

      const { data, error } = await supabase
        .from('ach_transactions')
        .select('*')
        .in('payroll_entry_id', filteredPayrollEntries.map(e => e.id));

      if (!error && data) {
        const transactionMap: Record<string, any> = {};
        data.forEach(tx => {
          transactionMap[tx.payroll_entry_id] = tx;
        });
        setAchTransactions(transactionMap);
      }
    } catch (error) {
      console.error('Error voiding transaction:', error);
      setVoidError(error instanceof Error ? error.message : 'Failed to void transaction');
      alert(`Failed to void transaction: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setVoidingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(entry.id);
        return newSet;
      });
    }
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!scrollContainerRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
    scrollContainerRef.current.style.cursor = 'grabbing';
    scrollContainerRef.current.style.userSelect = 'none';
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging || !scrollContainerRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    if (scrollContainerRef.current) {
      scrollContainerRef.current.style.cursor = 'grab';
      scrollContainerRef.current.style.userSelect = 'auto';
    }
  };

  const handleMouseLeave = () => {
    if (isDragging) {
      setIsDragging(false);
      if (scrollContainerRef.current) {
        scrollContainerRef.current.style.cursor = 'grab';
        scrollContainerRef.current.style.userSelect = 'auto';
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Loading payroll history...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Payroll History</h2>
        <div className="flex items-center gap-4">
          {isPolling && activeTransactionCount > 0 && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <RefreshCw className="h-4 w-4 animate-spin text-blue-600" />
              <span>Checking {activeTransactionCount} ACH transaction{activeTransactionCount > 1 ? 's' : ''}...</span>
              {lastUpdate && (
                <span className="text-xs text-gray-500">
                  Last: {lastUpdate.toLocaleTimeString()}
                </span>
              )}
            </div>
          )}
          {activeTransactionCount === 0 && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span className="text-xs">
                {lastUpdate ? 'All ACH transactions in final status' : 'No active ACH transactions'}
              </span>
            </div>
          )}
          <button
            onClick={() => pollNow()}
            disabled={isCheckingNow}
            className={`flex items-center gap-2 px-3 py-2 text-sm border rounded-md transition-colors ${
              isCheckingNow
                ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'border-blue-600 bg-blue-600 text-white hover:bg-blue-700 cursor-pointer'
            }`}
            title={isCheckingNow ? "Checking status..." : "Check ACH Status Now"}
          >
            <RefreshCw className={`h-4 w-4 ${isCheckingNow ? 'animate-spin' : ''}`} />
            {isCheckingNow ? 'Checking...' : 'Check Status'}
          </button>
        </div>
      </div>

      {(deleteError || updateError || voidError) && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{deleteError || updateError || voidError}</div>
        </div>
      )}

      {filteredPayrollEntries.length === 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">No Recent Payroll History</h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>Showing payroll entries from Period 45 onwards. No entries found in this range.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        <div
          ref={scrollContainerRef}
          className="overflow-x-auto cursor-grab active:cursor-grabbing"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
        >
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Period
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Employee
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pay Period
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Regular Pay
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Bonus
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Gross Pay
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Deductions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Net Pay
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPayrollEntries.map((entry) => {
                const employee = employees.find(e => e.id === entry.employeeId);
                const totalDeductions = entry.deductions.tax +
                  entry.deductions.insurance +
                  entry.deductions.retirement;
                const regularPay = entry.grossPay - (entry.bonus || 0);

                const achTx = achTransactions[entry.id];
                const showVoidButton = achTx && achTx.transaction_id &&
                                      (achTx.status === 'pending' ||
                                       achTx.status === 'created' ||
                                       achTx.status === 'processing' ||
                                       achTx.status === 'held');

                if (achTx) {
                  console.log(`Entry ${entry.id} ACH:`, {
                    has_transaction: !!achTx,
                    transaction_id: achTx.transaction_id,
                    status: achTx.status,
                    showVoidButton
                  });
                }

                return (
                  <tr key={entry.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatPeriodNumber(entry)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {employee?.firstName} {employee?.lastName}
                      </div>
                      <div className="text-sm text-gray-500">{employee?.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {parseLocalDateForDisplay(entry.payPeriod.start).toLocaleDateString()} - 
                      {parseLocalDateForDisplay(entry.payPeriod.end).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(regularPay)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                      {entry.bonus ? formatCurrency(entry.bonus) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(entry.grossPay)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{formatCurrency(totalDeductions)}</div>
                      <button
                        onClick={() => {
                          setSelectedEntry(entry);
                          setShowModal(true);
                        }}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        View Details
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(entry.netPay)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        <button
                          onClick={() => handleUpdateStatus(entry)}
                          className={`px-3 py-1 text-sm rounded-full transition-colors cursor-pointer ${
                            entry.status === 'paid'
                              ? 'bg-green-100 text-green-800 hover:bg-green-200'
                              : entry.status === 'pending'
                              ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                              : entry.status === 'processing'
                              ? 'bg-orange-100 text-orange-800 hover:bg-orange-200'
                              : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                          }`}
                          title={entry.status === 'pending' ? 'Click to mark as paid' : undefined}
                        >
                          {entry.status.charAt(0).toUpperCase() + entry.status.slice(1)}
                        </button>
                        {achTransactions[entry.id] && achTransactions[entry.id].status ? (
                          <div className={`text-xs px-2 py-0.5 rounded inline-block ${
                            achTransactions[entry.id].status === 'settled'
                              ? 'bg-green-50 text-green-700'
                              : achTransactions[entry.id].status === 'processing'
                              ? 'bg-blue-50 text-blue-700'
                              : achTransactions[entry.id].status === 'created'
                              ? 'bg-yellow-50 text-yellow-700'
                              : achTransactions[entry.id].status === 'held'
                              ? 'bg-orange-50 text-orange-700'
                              : achTransactions[entry.id].status === 'voided'
                              ? 'bg-red-50 text-red-700'
                              : 'bg-gray-50 text-gray-700'
                          }`}>
                            ACH: {achTransactions[entry.id].status}
                          </div>
                        ) : entry.payment_method === 'manual' && (entry.status === 'pending' || entry.status === 'paid') ? (
                          <div className="text-xs px-2 py-0.5 rounded inline-block bg-gray-50 text-gray-600">
                            ACH: Not initiated
                          </div>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-3">
                        <button
                          onClick={() => {
                            setSelectedEntry(entry);
                            setShowModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-900"
                          title="View Paystub"
                        >
                          <Eye className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDownloadPDF(entry)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Download PDF"
                        >
                          <Download className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleEmailPaystub(entry)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Email Paystub"
                        >
                          <Mail className="h-5 w-5" />
                        </button>
                        {showVoidButton && (
                          <button
                            onClick={() => handleVoidTransaction(entry)}
                            disabled={voidingIds.has(entry.id)}
                            className={`${
                              voidingIds.has(entry.id)
                                ? 'text-gray-400 cursor-not-allowed'
                                : 'text-orange-600 hover:text-orange-900'
                            }`}
                            title="Void ACH Transaction"
                          >
                            <XCircle className="h-5 w-5" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(entry)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete Entry"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && selectedEntry && (
        <PayStubModal
          entry={selectedEntry}
          employee={employees.find(e => e.id === selectedEntry.employeeId)!}
          onClose={() => {
            setShowModal(false);
            setSelectedEntry(null);
          }}
          onDownload={() => handleDownloadPDF(selectedEntry)}
          onEmail={() => handleEmailPaystub(selectedEntry)}
        />
      )}
    </div>
  );
};

export default PayrollHistory;